import { SetStateAction, useCallback, useEffect, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { formatDateWithPattern } from '@/utils/formatting';

const URL_DATE_FORMAT = 'MM-dd-yyyy';

/**
 * Fertilization date for the stripping page. When none is selected, today's
 * date is pinned into the URL so the page doesn't move to the next fert day
 * at midnight during night stripping.
 */
export function useFertilizationDate(): string {
  const [params, setParams] = useSearchParams();
  const dateParam = params.get('date');
  const [today] = useState(() =>
    formatDateWithPattern(new Date(), URL_DATE_FORMAT),
  );

  useEffect(() => {
    if (dateParam) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('date', today);
        return next;
      },
      { replace: true },
    );
  }, [dateParam, today, setParams]);

  return dateParam || today;
}

const qcKey = (date: string, siteId: string | null | undefined) =>
  `stripping-qc-lot:${date}:${siteId ?? ''}`;

const readLot = (key: string) => {
  try {
    return sessionStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
};

const writeLot = (key: string, lot: string) => {
  try {
    if (lot) sessionStorage.setItem(key, lot);
    else sessionStorage.removeItem(key);
  } catch {
    // storage unavailable: keep the in-memory value only
  }
};

/**
 * QC stripper tip lot number, remembered per fertilization date and site for
 * the browser session so re-renders, refreshes and navigation don't drop it.
 */
export function useQcLotNumber(
  date: string,
  siteId: string | null | undefined,
): [string, (value: SetStateAction<string>) => void] {
  const key = qcKey(date, siteId);
  const [state, setState] = useState(() => ({ key, lot: readLot(key) }));

  let current = state;
  if (state.key !== key) {
    current = { key, lot: readLot(key) };
    setState(current);
  }

  const setLot = useCallback((value: SetStateAction<string>) => {
    setState((prev) => {
      const lot = typeof value === 'function' ? value(prev.lot) : value;
      writeLot(prev.key, lot);
      return { key: prev.key, lot };
    });
  }, []);

  return [current.lot, setLot];
}
